/**
 * Get channel name for an organization
 * Format: private-org-{orgId}
 */
export function getOrgChannelName(orgId: string): string {
  return `private-org-${orgId}`;
}

/**
 * Get public channel name for a chat (for unauthenticated web-embed chats)
 * Format: public-chat-{chatId}
 */
export function getPublicChatChannelName(chatId: string): string {
  return `public-chat-${chatId}`;
}

/**
 * Get public channel name for agent creation progress (token-scoped, unauthenticated)
 * Format: public-agent-creation-{token}
 */
export function getAgentCreationProgressChannelName(token: string): string {
  return `public-agent-creation-${token}`;
}

/**
 * Event types for Pusher messages
 */
export enum PusherEventType {
  NEW_MESSAGE = 'new-message',
  CHAT_UPDATED = 'chat-updated',
  CHAT_STATUS_CHANGED = 'chat-status-changed',
  MESSAGES_UPDATED = 'messages-updated',
  CAMPAIGN_UPDATED = 'campaign-updated',
  SUBSCRIPTION_UPDATED = 'subscription-updated', // Triggers app refresh for subscription changes
  AGENT_CREATION_PROGRESS = 'agent-creation-progress',
  ONBOARDING_V2_EXPLORATION_PROGRESS = 'onboarding-v2-exploration-progress',
  ONBOARDING_V2_FINISH_UPDATED = 'onboarding-v2-finish-updated',
  FEEDBACK_UPDATED = 'feedback-updated',
  AGENT_TYPING = 'agent-typing',
  AGENT_TYPING_STOP = 'agent-typing-stop',
  MESSAGE_GUARDRAILS_RUNNING = 'message-guardrails-running',
  MESSAGE_GUARDRAILS_FAILED = 'message-guardrails-failed',
  MESSAGE_GUARDRAILS_READY = 'message-guardrails-ready',
  REACH_SUCCESS_RESULT = 'reach-success-result',
}

/**
 * Type definitions for Pusher event payloads
 */

/**
 * Lightweight message snapshot included in the new-message event so clients can
 * insert it directly into their SWR cache without a round-trip to the API.
 * Shape mirrors the response body from GET /api/chats/[chatId]/messages.
 */
export interface NewMessageEventMessage {
  id: string;
  role: string;
  parts: unknown;
  attachments: unknown;
  createdAt: string; // ISO 8601 string (Date serialised for JSON)
  sourceId?: string | null;
}

export interface NewMessageEvent {
  chatId: string;
  messageId: string;
  orgId: string;
  /** Full message data for direct SWR cache insertion (avoids extra API round-trip). */
  message?: NewMessageEventMessage;
}

export interface ChatUpdatedEvent {
  chatId: string;
  orgId: string;
}

export interface ChatStatusChangedEvent {
  chatId: string;
  orgId: string;
  status: string;
}

export interface MessagesUpdatedEvent {
  chatId: string;
  orgId: string;
  /** When true, refresh UI without a toast. All `messages-updated` events use this; toasts are only for `new-message`. */
  silent?: boolean;
  /**
   * Assistant message snapshot for direct SWR cache insertion (avoids extra API round-trip).
   * Only included when a new assistant message was just saved - omitted for metadata-only updates.
   * Clients must fall back to a full re-fetch if this field is absent.
   */
  message?: NewMessageEventMessage;
}

/**
 * Build a messages-updated payload that refreshes clients without a toast.
 * Message toasts are only shown for `new-message` (inbound customer messages).
 */
export function messagesUpdatedPayload(
  chatId: string,
  orgId: string,
  extra?: Pick<MessagesUpdatedEvent, 'message'>,
): MessagesUpdatedEvent {
  return { chatId, orgId, silent: true, ...extra };
}

export interface AgentTypingEvent {
  chatId: string;
  orgId: string;
}

export interface ReachSuccessResultEvent {
  chatId: string;
  orgId: string;
  status: 'done' | 'timeout' | 'error';
  summary?: string;
  suggestedReply?: string;
  urgency?: 'high' | 'medium' | 'low';
  cursorAgentId?: string;
}

export interface FeedbackUpdatedPusherEvent {
  feedbackId: string;
  chatId: string;
  messageId: string;
  /** Clerk user ID of the submitter — clients filter so only the author sees the toast */
  userId: string;
  state:
    | 'running'
    | 'auto_approved'
    | 'needs_approval'
    | 'failed'
    | 'limit_reached';
  /** Short summary for the success toast (present on auto_approved) */
  summary?: string;
  /** AgentChanges row ID for the "Review" link (present on needs_approval) */
  changeId?: string;
  /** Error message (present on failed) */
  error?: string;
}

